<template>
  <div class="row">
    <div class="col-lg-12 col-xxl-12">
      <div class="card card-custom card-stretch gutter-b">
        <div class="card-header border-0 py-5">
          <h3 class="card-title align-items-start flex-column"></h3>
          <div class="card-toolbar">
            <b-button
              variant="primary"
              class="float-right"
              style="padding-top: 6px; padding-bottom: 6px"
              v-on:click.prevent="
                $router.push('campaign/form/campaign_receipt')
              "
            >
              {{ $t("PAGE.CAMPAIGN.TABLE.BUTTON.NEW_CAMPAIGN_RECEIPT") }}
            </b-button>
          </div>
        </div>

        <div class="card-body pb-10 pt-10 page-container">
          <div class="col-lg-6 p-0">
            <b-form-group>
              <b-input-group size="sm">
                <b-form-input
                  v-model="pagination.filter"
                  :placeholder="$t('PAGE.DEFAULT.TABLE.SEARCH')"
                  type="search"
                  id="filterInput"
                  debounce="500"
                  @update="fetchData()"
                ></b-form-input>
                <b-input-group-append>
                  <b-button
                    :disabled="!pagination.filter"
                    @click="
                      pagination.filter = '';
                      fetchData();
                    "
                    >{{ $t("PAGE.DEFAULT.TABLE.CLEAR") }}</b-button
                  >
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </div>

          <div class="table-content">
            <div class="table-responsive">
              <b-table
                id="organizations-table"
                :striped="false"
                :items="items"
                :fields="fields"
                :sort-by.sync="pagination.orderBy"
                :sort-desc.sync="pagination.isDesc"
                :no-sort-reset="true"
                :busy.sync="isBusy"
                :loading="false"
                :borderless="false"
                head-variant="light"
                @sort-changed="fetchData()"
              >
                <template #cell(id)="data">
                  <div class="text-right">
                    <span v-if="pagination.isDesc">{{
                      total -
                        (pagination.perPage * (pagination.page - 1) +
                          data.index)
                    }}</span>
                    <span v-if="!pagination.isDesc">
                      {{
                        pagination.perPage * (pagination.page - 1) +
                          data.index +
                          1
                      }}
                    </span>
                  </div>
                </template>

                <template #cell(code)="data">
                  <div class="text-left">
                    <a
                      :href="`${liff_client_url}/?id=${data.item.code}`"
                      @click.stop
                      target="_blank"
                      ><u
                        >{{ `${liff_client_url}/?id=${data.item.code}` }}
                        <span class="svg-icon svg-icon-1x svg-icon-dark">
                          <inline-svg
                            src="media/svg/icons/Communication/Share.svg"
                          />
                        </span>
                      </u>
                    </a>
                  </div>
                </template>

                <template #cell(is_active)="data">
                  <div class="text-left">
                    <span v-if="data.item.is_active">{{
                      $t("PAGE.DEFAULT.TABLE.FIELDS.STATUS.ACTIVE")
                    }}</span>
                    <span v-else>{{
                      $t("PAGE.DEFAULT.TABLE.FIELDS.STATUS.INACTIVE")
                    }}</span>
                  </div>
                </template>

                <template #cell(action)="data">
                  <a
                    href="javascript:"
                    @click.prevent="
                      $router.push(
                        `campaign/form/campaign_receipt/${data.item.id}`
                      )
                    "
                    class="btn btn-icon btn-light btn-sm"
                    v-b-tooltip.hover
                    :title="$t('PAGE.DEFAULT.TABLE.FIELDS.ACTION.EDIT')"
                  >
                    <span class="svg-icon svg-icon-md svg-icon-success">
                      <inline-svg
                        src=" media/svg/icons/General/Edit.svg"
                      ></inline-svg>
                    </span> </a
                  >&nbsp;&nbsp;&nbsp;
                  <a
                    v-if="!data.item.is_active"
                    href="javascript:"
                    @click.prevent="removeData(data.item.id)"
                    class="btn btn-icon btn-light btn-sm"
                    v-b-tooltip.hover
                    :title="$t('PAGE.DEFAULT.TABLE.FIELDS.ACTION.DELETE.TITLE')"
                  >
                    <span class="svg-icon svg-icon-md svg-icon-danger">
                      <inline-svg
                        src="media/svg/icons/General/Trash.svg"
                      ></inline-svg>
                    </span>
                  </a>
                </template>
              </b-table>
              <p class="text-center pt-3" v-if="items.length < 1">
                {{ $t("PAGE.DEFAULT.TABLE.DATA_NOT_FOUND") }}
              </p>
            </div>

            <div class="row justify-content-between">
              <div div class="col-lg-3">
                <b-form-group
                  id="name-input-group"
                  :label="$t('PAGE.DEFAULT.TABLE.PER_PAGE')"
                  label-for="name-input"
                  label-cols="5"
                  label-align-sm="left"
                  label-size="lg"
                >
                  <b-form-select
                    v-model="pagination.perPage"
                    id="perPageSelect"
                    size="sm"
                    :options="paginationOptions"
                    @change="fetchData()"
                  ></b-form-select>
                </b-form-group>
              </div>

              <div div class="col-lg-6">
                <b-pagination
                  v-model="pagination.page"
                  :total-rows="total"
                  :per-page="pagination.perPage"
                  @input="fetchData()"
                  align="right"
                ></b-pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import Swal from "sweetalert2";
import {
  PAGINATION_CAMPAIGN,
  SET_PAGINATION,
  SET_IS_CLEAR_PAGINATION,
  CLEAR_PAGINATION,
  DELETE_CAMPAIGN
} from "@/core/services/store/campaign.module";
import { Config } from "@/core/config/variable.config";

export default {
  name: "Campaign",
  data() {
    return {
      fields: [
        {
          key: "id",
          label: `${this.$t("PAGE.DEFAULT.TABLE.FIELDS.INDEX")}`,
          key_table: "id",
          sortable: true,
          isFilter: false,
          tdClass: "idField"
        },
        {
          key: "name",
          label: `${this.$t("PAGE.DEFAULT.TABLE.FIELDS.NAME")}`,
          key_table: "name",
          sortable: true,
          isFilter: true
        },
        {
          key: "code",
          label: `${this.$t("PAGE.CAMPAIGN.TABLE.FIELDS.CAMPAIGN_URL")}`,
          key_table: "code",
          sortable: false,
          isFilter: true,
          tdClass: "codeField"
        },
        {
          key: "start_date",
          label: `${this.$t("PAGE.CAMPAIGN.TABLE.FIELDS.START_DATE")}`,
          key_table: "start_date",
          sortable: true,
          isFilter: true
        },
        {
          key: "end_date",
          label: `${this.$t("PAGE.CAMPAIGN.TABLE.FIELDS.END_DATE")}`,
          key_table: "end_date",
          sortable: true,
          isFilter: true
        },
        {
          key: "is_active",
          label: `${this.$t("PAGE.DEFAULT.TABLE.FIELDS.STATUS.TITLE")}`,
          key_table: "is_active",
          sortable: true,
          isFilter: true
        },
        {
          key: "action",
          label: `${this.$t("PAGE.DEFAULT.TABLE.FIELDS.ACTION.TITLE")}`,
          sortable: false,
          isFilter: false,
          tdClass: "actionField"
        }
      ],
      select: [
        "id",
        "name",
        "code",
        "start_date",
        "end_date",
        "is_active",
        "status_id"
      ],
      include: [],
      paginationOptions: [25, 50, 100, 200],
      isBusy: false,
      items: [],
      total: 0,
      liff_client_url: null
    };
  },
  computed: {
    isClearPagination: {
      get() {
        return this.$store.state.campaign.isClearPagination;
      },
      set(value) {
        this.$store.commit(SET_IS_CLEAR_PAGINATION, {
          isClearPagination: value
        });
      }
    },
    pagination: {
      get() {
        return this.$store.state.campaign.pagination;
      },
      set(value) {
        this.$store.commit(SET_PAGINATION, { pagination: value });
      }
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: `${this.$t("MENU.CAMPAIGN_MANAGEMENT.CAMPAIGN")}` }
    ]);
    if (this.isClearPagination) {
      this.$store.commit(CLEAR_PAGINATION);
    }
    this.isClearPagination = true;
    this.liff_client_url = Config.SWEEPSTAKES_LIFF_CLIENT_URL;
    this.fetchData();
  },
  methods: {
    getParams() {
      const orderByIndex = this.fields.findIndex(
        item => item.key === this.pagination.orderBy.toString()
      );
      return {
        page: this.pagination.page.toString(),
        perPage: this.pagination.perPage.toString(),
        orderBy: orderByIndex ? this.fields[orderByIndex].key_table : "id",
        isDesc: this.pagination.isDesc.toString(),
        filter: this.pagination.filter.toString(),
        filterBy: `${this.fields
          .map(item => {
            if (item.isFilter) return item.key;
          })
          .filter(item => !!item)}`,
        select: `${this.select}`,
        include: `${this.include}`
      };
    },
    fetchData() {
      this.$store
        .dispatch(PAGINATION_CAMPAIGN, { params: this.getParams() })
        .then(({ data }) => {
          if (data.status === "success" && data.data) {
            this.items = data.data.data;
            this.total = data.data.total;
            if (this.total > 0 && this.items.length < 1) {
              this.$store.commit(CLEAR_PAGINATION);
              this.fetchData();
            }
          } else if (data.status === "error" && data.error) {
            Swal.fire({
              icon: "error",
              text: `${this.$t("RESPONSE_MESSAGE." + data.error)}`,
              showConfirmButton: true,
              confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
            });
          }
          this.isBusy = false;
        })
        .catch(error => {
          Swal.fire({
            icon: "error",
            text: `${error.error}`,
            showConfirmButton: true,
            confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
          });
          this.isBusy = false;
        });
    },
    removeData(id) {
      Swal.fire({
        text: `${this.$t("PAGE.DEFAULT.TABLE.FIELDS.ACTION.DELETE.DESC")}`,
        icon: "warning",
        showCancelButton: true,
        showConfirmButton: true,
        confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`,
        cancelButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_CANCEL")}`
      }).then(result => {
        if (result.isConfirmed) {
          this.$store
            .dispatch(DELETE_CAMPAIGN, { id })
            .then(({ data }) => {
              if (data.status === "success") {
                Swal.fire({
                  icon: "success",
                  text: `${this.$t("RESPONSE_MESSAGE." + data.data)}`,
                  confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
                });
                this.fetchData();
              } else if (data.status === "error" && data.error) {
                Swal.fire({
                  icon: "error",
                  text: `${this.$t("RESPONSE_MESSAGE." + data.error)}`,
                  confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
                });
              }
              this.isBusy = false;
            })
            .catch(error => {
              Swal.fire({
                icon: "error",
                text: `${error.error}`,
                confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
              });
              this.isBusy = false;
            });
        }
      });
    }
  }
};
</script>

<style>
.codeField {
  max-width: 300px !important;
}
</style>
